/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import EnterpriseSearchBarLegacyGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseSearchBarLegacyGridItem';
import {
  getEnterpriseLogoLabel,
  getEnterpriseProgramLogos,
  getLinkForEnterpriseProgram,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import LanguageDropdownLegacyGridItem from 'bundles/composable-page-header/components/grid-items/LanguageDropdownLegacyGridItem';
import NavAiSparkleGridItem from 'bundles/composable-page-header/components/grid-items/NavAiSparkleGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import ProgramSwitcherGridItem from 'bundles/composable-page-header/components/grid-items/ProgramSwitcherGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';
import useUnifiedEnterpriseSearch from 'bundles/search-common/hooks/useUnifiedEnterpriseSearch';

const styles = {
  flexGrow: css`
    flex-grow: 1;
  `,
};

type Props = {
  showDiscoveryElements?: boolean;
};

const EnterpriseLearnerOnConsumerDesktopMainNav = ({ showDiscoveryElements = true }: Props) => {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
  } = useConsumerMyPageHeader();
  const shouldShowHelpDropdown = useShouldShowHelpDropdown();
  const showUnifiedSearch = useUnifiedEnterpriseSearch();

  if (!chosenEnterpriseProgram) {
    return null;
  }

  return (
    <MainNavigationWrapper>
      <Grid item md={8} container alignItems="center" spacing={16}>
        <Grid item>
          <ProgramSwitcherGridItem
            currentProgram={chosenEnterpriseProgram}
            currentDegree={chosenDegree}
            programs={enrolledEnterprisePrograms}
            degrees={enrolledDegreePrograms}
          />
        </Grid>
        <Grid item>
          <HomeLogoGridItemModule
            href={getLinkForEnterpriseProgram(chosenEnterpriseProgram)}
            logos={getEnterpriseProgramLogos(chosenEnterpriseProgram)}
            ariaLabel={getEnterpriseLogoLabel(chosenEnterpriseProgram)}
          />
        </Grid>
        {showDiscoveryElements && (
          <Grid item css={styles.flexGrow}>
            {showUnifiedSearch ? (
              <EnterpriseOrgScopedSearchBarGridItem
                thirdPartyOrganizationSlug={chosenEnterpriseProgram.naptimeThirdPartyOrganization?.slug}
                enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                chosenProgramSlug={chosenEnterpriseProgram.slug}
                chosenProgramId={chosenEnterpriseProgram.id}
              />
            ) : (
              <EnterpriseSearchBarLegacyGridItem currentProgram={chosenEnterpriseProgram} />
            )}
          </Grid>
        )}
      </Grid>
      <Grid item container md={4} alignItems="center" justifyContent="flex-end">
        {shouldShowHelpDropdown && (
          <Grid item>
            <HelpDropdownGridItem disableChatSsr={true} />
          </Grid>
        )}
        <Grid item>
          <NavAiSparkleGridItem />
        </Grid>
        <LanguageDropdownLegacyGridItem
          currentThirdPartyOrgId={chosenEnterpriseProgram?.naptimeThirdPartyOrganization?.id}
        />
        <Grid item>
          <NotificationsLegacyGridItem />
        </Grid>
        {showCoachStudio() && (
          <Grid item>
            <CoachStudioGridItem />
          </Grid>
        )}
        <Grid item>
          <DefaultAccountDropdownGridItemRenderer
            hasEnrolledPrograms={!!hasEnrolledPrograms}
            naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
            hasCheatingIncidents={!!hasCheatingIncidents}
            canViewEducatorAdminLink={!!canViewEducatorAdminLink}
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
          />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default EnterpriseLearnerOnConsumerDesktopMainNav;
